/**
 * Set margin and padding for first label element to overlap `\` symbol
 * to avoid blinking on mouse move from first label element to second
 * @param labelEl {HTMLElement}
 * @param separatorEl {HTMLElement}
 */
function setLabelToFillSeparator(labelEl, separatorEl) {
  setTimeout(() => {
    const { width } = separatorEl.getBoundingClientRect();
    // eslint-disable-next-line no-param-reassign
    labelEl.style.marginRight = `-${width}px`;
    // eslint-disable-next-line no-param-reassign
    labelEl.style.paddingRight = `${width}px`;
  });
}

/**
 * Factory to create label element with styles and event listeners
 * @returns {HTMLSpanElement}
 */
function getElForLabel() {
  const el = document.createElement('span');
  // position and z-index here to later overlap separator element
  el.style.cssText = `
    font-weight: 600;
    cursor: pointer;
    position: relative;
    z-index: 1;
  `;
  // Set highlighting label element on mouse over
  el.addEventListener('mouseover', () => {
    el.style.color = '#12171C';
  });
  el.addEventListener('mouseout', () => {
    el.style.color = null;
  });
  return el;
}

/**
 * Get raw label element and divided text by words to elements in order to manipulate them
 * Split text by separator ' / ', then get first word of each element and create special element for it
 * Separator has element as well to get width of element and overlap first label element over separator
 * If no separator symbol in label string, then throw error without any changes
 *
 * @param labelEl {HTMLElement}
 * @returns {{labelEl2: HTMLSpanElement, labelEl1: HTMLSpanElement}}
 */
function prepareLabelElement(labelEl) {
  const labelTextSplit = labelEl.textContent.split(' / ');
  if (labelTextSplit.length < 2) throw new Error('v-params-highlightify: No changes needed');
  const labelEl1 = getElForLabel();
  const labelEl2 = getElForLabel();
  const separatorEl = document.createElement('span');
  const label1TextSplit = labelTextSplit[0].split(' ');
  labelEl1.textContent = label1TextSplit.shift();
  const label1TextRest = label1TextSplit.join(' ');
  const label2TextSplit = labelTextSplit[1].split(' ');
  labelEl2.textContent = ` ${label2TextSplit.shift()}`;
  const label2TextRest = label2TextSplit.join(' ');
  // eslint-disable-next-line no-param-reassign
  labelEl.innerHTML = '';
  labelEl.append(labelEl1);
  if (label1TextRest) {
    labelEl.append(` ${label1TextRest} `);
  } else {
    labelEl1.append(' ');
  }
  separatorEl.append('/');
  labelEl.append(separatorEl);
  labelEl.append(labelEl2);
  if (label2TextRest) labelEl.append(` ${label2TextRest}`);
  // To overlap separator by first label to avoid blinking while mouse move over separator
  setLabelToFillSeparator(labelEl1, separatorEl);
  return { labelEl1, labelEl2 };
}

/**
 * Get raw value element and divided text by separator ' / ' to elements in order to manipulate them
 * If no separator symbol in label string, then throw error without any changes
 * @param valueEl {HTMLElement}
 * @returns {{valueEl2: HTMLSpanElement, valueEl1: HTMLSpanElement}}
 */
function prepareValueElement(valueEl) {
  const valueTextSplit = valueEl.textContent.split(' / ');
  if (valueTextSplit.length < 2) throw new Error('v-params-highlightify: No changes needed');
  const valueEl1 = document.createElement('span');
  const valueEl2 = document.createElement('span');
  [valueEl1.textContent, valueEl2.textContent] = valueTextSplit;
  // eslint-disable-next-line no-param-reassign
  valueEl.innerHTML = '';
  valueEl.append(valueEl1);
  valueEl.append(' / ');
  valueEl.append(valueEl2);
  return { valueEl1, valueEl2 };
}

/**
 * Get label and value element and set event listeners
 * to play down value element when mouse over on label element to highlight another value element
 *
 * @param labelEl {HTMLElement}
 * @param valueEl {HTMLElement}
 */
function setLabelHoverEvents(labelEl, valueEl) {
  labelEl.addEventListener('mouseover', () => {
    // eslint-disable-next-line no-param-reassign
    valueEl.style.opacity = '.5';
  });
  labelEl.addEventListener('mouseout', () => {
    // eslint-disable-next-line no-param-reassign
    valueEl.style.opacity = 'inherit';
  });
}

export default {
  inserted(el, { value }) {
    // expect to get selectors for label and value elements
    if (!value || !value.labelSelector || !value.valueSelector) {
      throw new Error('Directive v-params-highlightify expect to get labelSelector and valueSelector');
    }

    try {
      const labelEl = el.querySelector(value.labelSelector);
      const valueEl = el.querySelector(value.valueSelector);
      // If no elements, no need to changes
      if (!labelEl || !valueEl) return;
      // Prepare elements to create property elements for words
      const { labelEl1, labelEl2 } = prepareLabelElement(labelEl);
      const { valueEl1, valueEl2 } = prepareValueElement(valueEl);
      // set mouse over events to highlight what is needed
      setLabelHoverEvents(labelEl1, valueEl2);
      setLabelHoverEvents(labelEl2, valueEl1);
    } catch (e) { /* skip all errors */ }
  },
};
